import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../api/client";
import type { Audience, UserSettings, Visibility } from "../api/types";

export function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [visibility, setVisibility] = useState<Visibility>("nothing");
  const [audience, setAudience] = useState<Audience>("team");

  const save = useMutation({
    mutationFn: (body: { visibility: Visibility; audience: Audience }) =>
      apiFetch<UserSettings>("/me/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (returned) => {
      queryClient.setQueryData(["my-settings"], returned);
      void queryClient.invalidateQueries({ queryKey: ["absences"] });
      void queryClient.invalidateQueries({ queryKey: ["people"] });
      navigate("/calendar");
    },
  });

  return (
    <section className="onboarding">
      <h2>{t("onboarding.title")}</h2>
      <p>{t("onboarding.intro")}</p>
      <p className="manager-notice">{t("onboarding.managerNotice")}</p>

      <fieldset>
        <legend>{t("onboarding.showLabel")}</legend>
        {(["nothing", "binary", "full"] as const).map((v) => (
          <label key={v}>
            <input
              type="radio"
              name="visibility"
              value={v}
              checked={visibility === v}
              onChange={() => setVisibility(v)}
            />
            {t(`onboarding.showOptions.${v}`)}
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>{t("onboarding.audienceLabel")}</legend>
        {(["team", "company"] as const).map((a) => (
          <label key={a}>
            <input
              type="radio"
              name="audience"
              value={a}
              checked={audience === a}
              onChange={() => setAudience(a)}
            />
            {t(`onboarding.audienceOptions.${a}`)}
          </label>
        ))}
      </fieldset>

      <button
        onClick={() => save.mutate({ visibility, audience })}
        disabled={save.isPending}
      >
        {t("onboarding.save")}
      </button>
    </section>
  );
}
